import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useClientPoint,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  type Placement,
} from "@floating-ui/react";
import useMelaTooltipStyles from "./MelaTooltip.styles";

const EXIT_ANIMATION_MS = 260;

export interface MelaTooltipProps {
  children: React.ReactNode;
  content?: React.ReactNode;
  placement?: Placement;
  disabled?: boolean;
  openDelay?: number;
  closeDelay?: number;
  offsetValue?: number;
  maxWidth?: number;
  fullWidth?: boolean;
  triggerAs?: "span" | "div";
  disableFlip?: boolean;
  anchorToCursorOnOpen?: boolean;
  interactive?: boolean;
  tone?: "dark" | "light";
  triggerStyle?: React.CSSProperties;
}

export const MelaTooltip = ({
  children,
  content,
  placement = "top",
  disabled = false,
  openDelay = 450,
  closeDelay = 80,
  offsetValue = 10,
  maxWidth = 260,
  fullWidth = false,
  triggerAs = "span",
  disableFlip = false,
  anchorToCursorOnOpen = false,
  interactive = false,
  tone = "dark",
  triggerStyle,
}: MelaTooltipProps) => {
  const [open, setOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [lockedCursorPoint, setLockedCursorPoint] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const arrowRef = useRef<SVGSVGElement | null>(null);
  const lastCursorPointRef = useRef<{ x: number; y: number } | null>(null);
  const classes = useMelaTooltipStyles({
    fullWidth,
    maxWidth,
    interactive,
    tone,
  });
  const arrowFill =
    tone === "light"
      ? "var(--mela-surface-card)"
      : "var(--mela-color-tooltip-bg)";
  const arrowStroke =
    tone === "light"
      ? "var(--mela-border-subtle)"
      : "var(--mela-color-tooltip-border)";
  const TriggerTag = triggerAs;

  const shouldRenderTooltip = Boolean(content) && !disabled;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen && anchorToCursorOnOpen && lastCursorPointRef.current) {
        setLockedCursorPoint(lastCursorPointRef.current);
      }

      if (!nextOpen) {
        setLockedCursorPoint(null);
      }

      setOpen(nextOpen);
    },
    [anchorToCursorOnOpen],
  );

  const handleReferenceMousePosition = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!anchorToCursorOnOpen || open) {
        return;
      }

      lastCursorPointRef.current = {
        x: event.clientX,
        y: event.clientY,
      };
    },
    [anchorToCursorOnOpen, open],
  );

  useEffect(() => {
    if (!shouldRenderTooltip) {
      setIsMounted(false);
      setIsExiting(false);
      return;
    }

    if (open) {
      setIsMounted(true);
      setIsExiting(false);
      return;
    }

    if (!isMounted) {
      return;
    }

    setIsExiting(true);
    const timeout = window.setTimeout(() => {
      setIsMounted(false);
      setIsExiting(false);
    }, EXIT_ANIMATION_MS);

    return () => window.clearTimeout(timeout);
  }, [isMounted, open, shouldRenderTooltip]);

  const middleware = useMemo(
    () => [
      offset(offsetValue),
      ...(!disableFlip
        ? [flip({ padding: 12, fallbackAxisSideDirection: "start" })]
        : []),
      shift({ padding: 12, crossAxis: true }),
      arrow({ element: arrowRef, padding: 10 }),
    ],
    [disableFlip, offsetValue],
  );

  const { refs, floatingStyles, context } = useFloating({
    placement,
    strategy: "fixed",
    open: shouldRenderTooltip ? open : false,
    onOpenChange: handleOpenChange,
    middleware,
    whileElementsMounted: autoUpdate,
  });

  const floatingLayerStyle = useMemo(
    () => ({
      ...floatingStyles,
      zIndex: 30050,
    }),
    [floatingStyles],
  );

  const hover = useHover(context, {
    move: false,
    handleClose: interactive ? safePolygon() : undefined,
    delay: {
      open: openDelay,
      close: closeDelay,
    },
  });
  const clientPoint = useClientPoint(context, {
    enabled: Boolean(anchorToCursorOnOpen && lockedCursorPoint),
    x: lockedCursorPoint?.x ?? null,
    y: lockedCursorPoint?.y ?? null,
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    clientPoint,
    focus,
    dismiss,
    role,
  ]);

  if (!shouldRenderTooltip) {
    return <>{children}</>;
  }

  return (
    <>
      <TriggerTag
        ref={refs.setReference}
        className={classes.trigger}
        {...getReferenceProps({
          onMouseEnter: handleReferenceMousePosition,
          onMouseMove: handleReferenceMousePosition,
        })}
        style={triggerStyle}
      >
        {children}
      </TriggerTag>
      {isMounted ? (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingLayerStyle}
            {...getFloatingProps()}
          >
            <div
              className={`${classes.content} ${
                isExiting ? classes.contentExit : classes.contentEnter
              }`.trim()}
            >
              {content}
              <FloatingArrow
                ref={arrowRef}
                context={context}
                className={classes.arrow}
                width={12}
                height={7}
                tipRadius={1}
                fill={arrowFill}
                stroke={arrowStroke}
                strokeWidth={1}
              />
            </div>
          </div>
        </FloatingPortal>
      ) : null}
    </>
  );
};
